<script lang="ts">
  import { logger } from "$lib/utils/logger";
  import { domainAndFileFromUrl } from "$lib/utils/urlUtils";
  import { contentPlayed } from "$lib/proofOfPlay/popManager";
  import { onDestroy, onMount } from "svelte";
  import { reportError } from "$lib/errorHandling/errorReporter";
  import { recordPlayEvent } from "$lib/dataPipeline/dataPipelineManager";
  import { getPopUrl } from "$lib/proofOfPlay/popHelpers";
  import type { ImageContent } from "$lib/elev8Api/contentTypes";
  import { t } from "$lib/utils/i18n";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";

  interface Props {
    content: ImageContent;
  }

  let { content: _content }: Props = $props();
  // svelte-ignore state_referenced_locally
  const content = _content;
  const popUrl = getPopUrl(content);
  const config = getConfigurationContext();

  // Only read in onDestroy (proof-of-play); not reactive.
  let imageLoaded = false;
  // Set on load OR error, so a broken creative stops the placeholder.
  let loadSettled = $state(false);
  let imgElement: HTMLImageElement | undefined = $state();

  onMount(() => {
    if (imgElement?.complete) {
      loadSettled = true;
      if (imgElement.naturalWidth > 0) {
        imageLoaded = true;
        logger.debug(
          "[ImageContent] image loaded (onMount - probably cached)",
          content.id
        );
      }
    }
  });

  function onload(event: Event) {
    const img = event.currentTarget as HTMLImageElement;
    loadSettled = true;
    if (img.complete && img.naturalWidth > 0) {
      imageLoaded = true;
      logger.debug("[ImageContent] image loaded (load event)", content.id);
    }
  }

  function onerror() {
    loadSettled = true;
  }

  onDestroy(() => {
    logger.debug(
      "[ImageContent] onDestroy - component unmounted",
      content.id
    );
    // DOM-state fallback: onload may not have fired for cached images
    const isLoadComplete = imgElement?.complete === true;
    const naturalWidth = imgElement?.naturalWidth ?? 0;
    const played = imageLoaded || (isLoadComplete && naturalWidth > 0);
    if (played) {
      logger.debug("[ImageContent] ..image displayed");
      recordPlayEvent(content);
      if (popUrl) {
        logger.info(
          "[ImageContent] image ad played (domain//file, pop URL): ",
          domainAndFileFromUrl(content.asset_url),
          domainAndFileFromUrl(popUrl),
          `id: ${content.id}`
        );
        contentPlayed(popUrl);
      }
    } else if (isLoadComplete && naturalWidth === 0) {
      // Broken creative; asset_url in tags (not message) for AppSignal grouping
      reportError("[ImageContent] image did not load", undefined, {
        asset_url: content.asset_url,
      });
    } else {
      logger.debug(
        "[ImageContent] component unmounted before image loaded",
        content.asset_url
      );
    }
  });
</script>

<div class="vc-content-layout">
  {#if !loadSettled}
    <div class="vc-loading-breathe" aria-hidden="true"></div>
  {/if}

  <img
    crossorigin="anonymous"
    class="vc-content-media"
    alt={t("alt.main", config.language)}
    src={content.asset_url}
    bind:this={imgElement}
    {onload}
    {onerror}
  />
</div>
